import React, { useEffect, useMemo, useState } from "react";
import "../css/LeaderBoard.css";
import {
  getMonthlyGastonIndex,
  getMonthlyOpenings,
  RankingLineNewProps,
} from "../data/getAxiosRequests";
import IconButton from "../components/UI/IconButton";
import { SVGs } from "../constants/Svgs";

// ⬇️ Adjust these paths if needed
type CommonItem = {
  user_id: string;
  username: string;
  first_name: string;
  last_name: string;
  profile_picture: string | null;
  climber_id: number;
  favorite_climbing_gym_name: string | null;
};

type IndexItem = CommonItem & {
  gender: string;
  index: number;
  rank: number;
};

type IndexPayload = {
  mixed: IndexItem[];
  male: IndexItem[];
  female: IndexItem[];
};

type SetterItem = CommonItem & {
  count_boulders: number;
  rank: number;
};

type Mode = "index" | "openings";
type GenderTab = "mixed" | "female" | "male";

const Medal: React.FC<{ rank: number }> = ({ rank }) => {
  if (rank === 1) return <span className="medal" title="1er">🥇</span>;
  if (rank === 2) return <span className="medal" title="2e">🥈</span>;
  if (rank === 3) return <span className="medal" title="3e">🥉</span>;
  return <span className="rank-number">{rank}</span>;
};

const Row: React.FC<{
  rank: number;
  avatar?: string | null;
  name: string;
  subtitle?: string;
  rightValue: string;
  barPercent: number; // 0..100
}> = ({ rank, avatar, name, subtitle, rightValue, barPercent }) => (
  <div className="row">
    <div className="rank">
      <Medal rank={rank} />
    </div>
    <img
      className="avatar"
      src={avatar || "/fallback-avatar.png"}
      alt={name}
      loading="lazy"
    />
    <div className="who regular">
      <div className="name lower" title={name}>{name}</div>
      <div className="meta lower2" title={subtitle || ""}>{subtitle || "—"}</div>
    </div>
    <div className="bar">
      <div className="bar-fill" style={{ width: `${Math.max(0, Math.min(100, barPercent))}%` }} />
    </div>
    <div className="value bold lower">{rightValue}</div>
  </div>
);

const SkeletonRows: React.FC = () => (
  <div className="skeleton">
    {Array.from({ length: 18 }).map((_, i) => (
      <div className="row" key={i}>
        <div className="rank skeleton-box" />
        <div className="avatar skeleton-box" />
        <div className="who lower2">
          <div className="name skeleton-box" style={{ width: 160, height: 12 }} />
          <div className="meta skeleton-box" style={{ width: 120, height: 10 }} />
        </div>
        <div className="bar skeleton-box" />
        <div className="value skeleton-box" />
      </div>
    ))}
  </div>
);

const EmptyState: React.FC<{ text: string }> = ({ text }) => (
  <div className="empty">{text}</div>
);

const Leaderboards: React.FC = () => {
  const [mode, setMode] = useState<Mode>("index");
  const [genderTab, setGenderTab] = useState<GenderTab>("mixed");

  const [indexData, setIndexData] = useState<IndexPayload | null>(null);
  const [openings, setOpenings] = useState<SetterItem[] | null>(null);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Lazy-fetch depending on the active mode
  useEffect(() => {
    let alive = true;
    setErr(null);

    const mapToIndexItems = (arr: RankingLineNewProps[]): IndexItem[] =>
      arr.map((it) => ({
        user_id: it.userId ?? "",
        username: it.username ?? (it.firstName || ""),
        first_name: it.firstName,
        last_name: it.lastName,
        profile_picture: it.profilePicture ?? null,
        climber_id: it.climberId ?? 0,
        favorite_climbing_gym_name: it.gymName ?? null,
        gender: "",
        index: it.points,
        rank: it.rank,
      }));

    const mapToSetterItems = (arr: RankingLineNewProps[]): SetterItem[] =>
      arr.map((it) => ({
        user_id: it.userId ?? "",
        username: it.username ?? (it.firstName || ""),
        first_name: it.firstName,
        last_name: it.lastName,
        profile_picture: it.profilePicture ?? null,
        climber_id: it.climberId ?? 0,
        favorite_climbing_gym_name: it.gymName ?? null,
        count_boulders: it.points,
        rank: it.rank,
      }));

    const run = async () => {
      try {
        setLoading(true);

        if (mode === "index") {
          if (indexData) return;
          const data = await getMonthlyGastonIndex();
          if (!alive) return;
          setIndexData({
            mixed: mapToIndexItems(data.mixed),
            female: mapToIndexItems(data.female),
            male: mapToIndexItems(data.male),
          });
        } else {
          if (openings) return;
          const list = await getMonthlyOpenings();
          if (!alive) return;
          setOpenings(mapToSetterItems(list));
        }
      } catch (e: any) {
        if (alive) setErr(e?.message || "Une erreur est survenue.");
      } finally {
        if (alive) setLoading(false);
      }
    };

    run();
    return () => { alive = false; };
  }, [mode, indexData, openings]);

  // Current list + bar max
  const currentIndexList: IndexItem[] = useMemo(() => {
    if (!indexData) return [];
    return indexData[genderTab];
  }, [indexData, genderTab]);

  const maxIndex = useMemo(
    () => (currentIndexList.length ? Math.max(...currentIndexList.map(x => x.index)) : 100),
    [currentIndexList]
  );
  const maxOpenings = useMemo(
    () => (openings && openings.length ? Math.max(...openings.map(x => x.count_boulders)) : 1),
    [openings]
  );

  /* ---- Dynamic month/year in French (capitalized) ---- */
  const monthLabel = useMemo(() => {
    const now = new Date();
    const month = now.toLocaleString("fr-FR", { month: "long" }); // e.g., "octobre"
    const capMonth = month.charAt(0).toUpperCase() + month.slice(1);
    return `${capMonth} ${now.getFullYear()}`;
  }, []);

  const title = mode === "index"
    ? `${monthLabel} — Les meilleurs grimpeurs de la communauté`
    : `Blocs ouverts — ${monthLabel}`;

  const bannerText = mode === "index"
    ? `Mesure-toi aux ${indexData?.mixed?.length ?? "…"} grimpeurs de la communauté, maximise ton Gaston Index 🔥`
    : `${openings?.length ?? "…"} ambassadeurs ce mois-ci, merci à tous !`;

  const footnoteText = mode === "index"
    ? "Le Gaston Index est un score sur 100 qui mesure ta capacité à grimper fort et à l’aise dans tous les styles. On prend tes 5 blocs les plus difficiles dans chaque style pour calculer ton score."
    : "L’ouverture de blocs permet aux salles communautaires d’être mises à jour pour les grimpeurs actifs sur Gaston.";

  return (
    <div className={`page ${mode === "index" ? "bg-purple" : "bg-green"}`}>
      {/* ==== Top Site Bar ==== */}
      <header className="sitebar">
        <div className="brand">
          <IconButton Svg={SVGs.gastonLogoRankingMonthly} />
        </div>
        {/**
        <nav className="site-links">
          <a className="link" href="#classements">Classements</a>
          <a className="link" href="#setter">Mon espace ouvreur</a>
          <a className="link" href="#contact">Nous rencontrer</a>
        </nav>
         */}
      </header>

      <main className="container container-wide">
        {/* ==== Big Toggle with subtitles ==== */}
        <div
          className={`big-toggle ${mode === "openings" ? "pos-openings" : "pos-index"}`}
          role="tablist "
          aria-label="Classements"
        >
          <button
            className={`big-pill ${mode === "index" ? "active" : ""}`}
            onClick={() => setMode("index")}
            role="tab"
            aria-selected={mode === "index"}
          >
            <span className="big-pill-title regular bold">Gaston Index</span>
            <span className="big-pill-sub regular">Progresse chaque mois</span>
          </button>
          <button
            className={`big-pill ${mode === "openings" ? "active" : ""}`}
            onClick={() => setMode("openings")}
            role="tab"
            aria-selected={mode === "openings"}
          >
            <span className="big-pill-title regular bold">Ouverture</span>
            <span className="big-pill-sub regular">Contribue pour la commu’</span>
          </button>
        </div>

        {/* ==== Center banner ==== */}
        <div className="hero-banner bold regular">{bannerText}</div>

        {/* ==== Main Panel ==== */}
        <section className="panel">
          <div className="panel-head">
            <div className="panel-title headings-nohemi-4-bold lower">
              <IconButton Svg={SVGs.whiteFlame} /> {title}
            </div>

            {mode === "index" && (
              <div className="tabs bold">
                <button
                  className={`tab ${genderTab === "mixed" ? "selectedRank" : ""}`}
                  onClick={() => setGenderTab("mixed")}
                >
                  Mixte
                </button>
                <button
                  className={`tab ${genderTab === "female" ? "selectedRank" : ""}`}
                  onClick={() => setGenderTab("female")}
                >
                  Femmes
                </button>
                <button
                  className={`tab ${genderTab === "male" ? "selectedRank" : ""}`}
                  onClick={() => setGenderTab("male")}
                >
                  Hommes
                </button>
              </div>
            )}
          </div>

          {err && <div className="error">{err}</div>}

          <div className="list list-tall">
            {loading ? (
              <SkeletonRows />
            ) : mode === "index" ? (
              currentIndexList.length === 0 ? (
                <EmptyState text="Aucun grimpeur pour le moment." />
              ) : (
                currentIndexList.map((it) => (
                  <Row
                    key={`${it.user_id}-${it.rank}`}
                    rank={it.rank}
                    avatar={it.profile_picture}
                    name={`${it.first_name} ${it.last_name}`.trim() || it.username}
                    subtitle={it.favorite_climbing_gym_name || "—"}
                    rightValue={String(it.index)}
                    barPercent={maxIndex ? (it.index / maxIndex) * 100 : 0}
                  />
                ))
              )
            ) : (
              !openings || openings.length === 0 ? (
                <EmptyState text="Aucun bloc ouvert pour le moment." />
              ) : (
                openings.map((it) => (
                  <Row
                    key={`${it.user_id}-${it.rank}`}
                    rank={it.rank}
                    avatar={it.profile_picture}
                    name={`${it.first_name} ${it.last_name}`.trim() || it.username}
                    subtitle={it.favorite_climbing_gym_name || "—"}
                    rightValue={String(it.count_boulders)}
                    barPercent={maxOpenings ? (it.count_boulders / maxOpenings) * 100 : 0}
                  />
                ))
              )
            )}
          </div>
        </section>

        {/* ==== Bottom footnote bar ==== */}
        <div className="footnote bold lower">{footnoteText}</div>
      </main>
    </div>
  );
};

export default Leaderboards;
